import type { ComponentPropsWithoutRef, JSX, MouseEvent, ReactNode } from 'react';
import { forwardRef, useState, useRef, useCallback, useMemo } from 'react';
import { styled } from 'styled-components';
import {
  isNil,
  isNotNil,
  isUndefined,
  isEmptyString,
  isString,
  isNonEmptyString,
} from '@wistia/type-guards';
import type { GradientNameType } from '../../private/helpers/getBackgroundGradient';
import { getBackgroundGradient } from '../../private/helpers/getBackgroundGradient';
import { ThumbnailStoryboardViewer } from './ThumbnailStoryboardViewer';

const WIDE_ASPECT_RATIO = 16 / 9; // eslint-disable-line @typescript-eslint/no-magic-numbers
const SQUARE_ASPECT_RATIO = 1;

type AspectRatioType = 'square' | 'wide';

const getAspectRatioValue = (aspectRatio: AspectRatioType): number => {
  if (aspectRatio === 'square') {
    return SQUARE_ASPECT_RATIO;
  }
  return WIDE_ASPECT_RATIO;
};

type ThumbnailImageProps = Pick<ThumbnailProps, 'thumbnailImageType'> & {
  thumbnailUrl: string;
};

type StyledThumbnailProps = {
  $backgroundUrl: string | undefined;
  $gradientBackground?: GradientNameType;
  $isScrubbable?: boolean;
};

type StyledThumbnailContainerProps = {
  $width: string;
  $aspectRatio: AspectRatioType;
  $isScrubbable?: boolean;
};

const WideThumbnailImage = styled.img`
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

const SquareThumbnailImage = styled.img`
  backdrop-filter: blur(8px);
  object-fit: contain;
  width: 100%;
`;

const ThumbnailImage = ({ thumbnailImageType, thumbnailUrl }: ThumbnailImageProps) => {
  if (thumbnailImageType === 'wide') {
    return (
      <WideThumbnailImage
        alt=""
        role="img"
        src={thumbnailUrl}
      />
    );
  }

  return (
    <SquareThumbnailImage
      alt=""
      role="img"
      src={thumbnailUrl}
    />
  );
};

const StyledThumbnailContainer = styled.div<StyledThumbnailContainerProps>`
  container-type: size;
  aspect-ratio: ${({ $aspectRatio }) => getAspectRatioValue($aspectRatio)};
  width: ${({ $width }) => $width};
  display: flex;
  overflow: hidden;
  ${({ $isScrubbable }) => $isScrubbable && 'cursor: pointer;'}
`;

const StyledThumbnail = styled.div<StyledThumbnailProps>`
  --wui-thumbnail-badge-offset: var(--wui-space-01);

  background-image: ${({ $backgroundUrl }) =>
    isNotNil($backgroundUrl) ? `url('${$backgroundUrl}')` : undefined};
  ${({ $backgroundUrl, $gradientBackground }) =>
    // if we don't have $backgroundUrl show a gradient
    isNil($backgroundUrl) ? getBackgroundGradient($gradientBackground) : undefined};
  background-position: center center;
  background-size: cover;
  display: flex;
  width: 100%;
  overflow: hidden;
  position: relative;

  &,
  img {
    border-radius: clamp(var(--wui-border-radius-01), 8cqh, var(--wui-border-radius-05));
  }

  @container (min-width: 200px) {
    --wui-thumbnail-badge-offset: var(--wui-space-02);
  }
`;

// note that this data structure is defined outside of this repo; potentially it could change
// from underneath us and this would need to be altered
type Storyboard = {
  __typename?: string;
  aspectRatio: number;
  frameCount: number;
  frameHeight: number;
  frameWidth: number;
  height: number;
  id?: string;
  url: string;
  width: number;
};

type StoryboardRendererProps = {
  storyboard: Storyboard;
  width: string;
  percent: number;
  cursorPosition: number | null;
  isStoryboardReady: boolean;
  aspectRatio: AspectRatioType;
};

const StoryboardRenderer = ({
  storyboard,
  width,
  percent,
  cursorPosition,
  isStoryboardReady,
  aspectRatio,
}: StoryboardRendererProps) => {
  const { url, width: sbWidth, height: sbHeight, frameHeight, frameWidth, frameCount } = storyboard;

  const targetWidth = isString(width) ? parseInt(width, 10) : Number(width);

  // ensure the scrub line only appears when the storyboard is fully loaded
  const effectiveCursorPosition = isStoryboardReady ? cursorPosition : null;

  return (
    <ThumbnailStoryboardViewer
      cursorPosition={effectiveCursorPosition}
      frameCount={frameCount}
      frameHeight={frameHeight}
      frameWidth={frameWidth}
      percent={percent}
      storyboardHeight={sbHeight}
      storyboardUrl={url}
      storyboardWidth={sbWidth}
      targetAspectRatio={getAspectRatioValue(aspectRatio)}
      targetWidth={targetWidth}
    />
  );
};

const getRelativeMousePosition = (elem: HTMLElement, mouseEvent: MouseEvent) => {
  const rect = elem.getBoundingClientRect();
  const relativeLeft = mouseEvent.clientX - rect.left;
  const relativeTop = mouseEvent.clientY - rect.top;

  return {
    top: relativeTop,
    left: relativeLeft,
  };
};

const hasValidThumbnailUrl = (thumbnailUrl: string | null | undefined) => {
  return isNotNil(thumbnailUrl) && isNonEmptyString(thumbnailUrl);
};

export type ThumbnailProps = Omit<ComponentPropsWithoutRef<'div'>, 'children'> & {
  /**
   * The desired display of the thumbnail, either wide or square
   */
  thumbnailImageType?: 'square' | 'wide' | undefined;
  /**
   * The URL of the thumbnail image to display
   */
  thumbnailUrl: string | null | undefined;
  /**
   * Children to render inside the thumbnail; must be a `ThumbnailBadge` component
   */
  children?: ReactNode;
  /**
   * The type of gradient to show either behind the icon or when there is no thumbnail image
   */
  gradientBackground?: GradientNameType;
  /**
   * The storyboard data for "scrubbing" functionality
   */
  storyboard?: Storyboard;
  /**
   * Width of the thumbnail. By default, the thumbnail will grow to fit its container.
   * Example: '320px'
   */
  width?: string;
  /**
   * The height of the thumbnail container when using storyboard.
   * Example: '180px'
   */
  height?: string;
  /**
   * The aspect ratio of the thumbnail container.
   * 'wide' maps to 16:9 ratio, 'square' maps to 1:1 ratio.
   */
  aspectRatio?: AspectRatioType;
};

/**
 * A `Thumbnail` is a small, reduced-size version of an image or video used as a preview or representative image.
 */
export const Thumbnail = forwardRef<HTMLDivElement, ThumbnailProps>(
  (
    {
      gradientBackground = 'defaultMidOne',
      thumbnailImageType = 'wide',
      thumbnailUrl,
      width = '100%',
      children,
      storyboard,
      height,
      aspectRatio = 'wide',
      ...props
    }: ThumbnailProps,
    ref,
  ): JSX.Element => {
    // keep the same order of useState calls as before for test compatibility
    const [percent, setPercent] = useState(0);
    const [isMouseOver, setIsMouseOver] = useState(false);
    const [isStoryboardReady, setIsStoryboardReady] = useState(false);
    const storyboardElementRef = useRef<HTMLImageElement | null>(null);
    const [cursorPosition, setCursorPosition] = useState<number | null>(null);

    // use thumbnailUrl as a background image if thumbnailImageType is square
    const backgroundUrl = useMemo(
      () =>
        thumbnailImageType === 'square' && hasValidThumbnailUrl(thumbnailUrl)
          ? thumbnailUrl
          : undefined,
      [thumbnailImageType, thumbnailUrl],
    );

    const isScrubbable = isNotNil(storyboard);

    const trackStoryboardLoadStatus = useCallback(() => {
      if (storyboardElementRef.current || !storyboard) {
        return storyboardElementRef.current;
      }

      // create an img element to utilize onload to track asset load status
      const asset = document.createElement('img');
      asset.onload = () => setIsStoryboardReady(true);
      asset.src = storyboard.url;

      storyboardElementRef.current = asset;
      return storyboardElementRef.current;
    }, [storyboard]);

    const handleMouseMove = useCallback(
      (mouseEvent: MouseEvent<HTMLDivElement>) => {
        if (!isScrubbable) {
          return;
        }

        const elem = mouseEvent.currentTarget;
        const relMousePos = getRelativeMousePosition(elem, mouseEvent);
        const newPercent = relMousePos.left / elem.clientWidth;

        trackStoryboardLoadStatus();

        setPercent(newPercent);
        setCursorPosition(relMousePos.left);
        setIsMouseOver(true);
      },
      [isScrubbable, trackStoryboardLoadStatus],
    );

    const handleMouseOut = useCallback(() => {
      if (!isScrubbable) {
        return;
      }
      setIsMouseOver(false);
      setCursorPosition(null);
    }, [isScrubbable]);

    const shouldRenderStoryboard = useMemo(() => {
      if (isNil(storyboard) || isUndefined(height) || isEmptyString(height)) {
        return false;
      }

      return isScrubbable && isMouseOver && isStoryboardReady;
    }, [isScrubbable, isMouseOver, isStoryboardReady, storyboard, height]);

    let thumbnailContent: React.ReactNode = null;
    if (storyboard && shouldRenderStoryboard) {
      thumbnailContent = (
        <StoryboardRenderer
          aspectRatio={aspectRatio}
          cursorPosition={cursorPosition}
          isStoryboardReady={isStoryboardReady}
          percent={percent}
          storyboard={storyboard}
          width={width}
        />
      );
    } else if (hasValidThumbnailUrl(thumbnailUrl)) {
      thumbnailContent = (
        <ThumbnailImage
          thumbnailImageType={thumbnailImageType}
          thumbnailUrl={thumbnailUrl}
        />
      );
    } else {
      thumbnailContent = null;
    }

    return (
      <StyledThumbnailContainer
        ref={ref}
        $aspectRatio={aspectRatio}
        $isScrubbable={isScrubbable}
        $width={width}
        data-wui-thumbnail-container={true}
        onBlur={handleMouseOut}
        onMouseMove={handleMouseMove}
        onMouseOut={handleMouseOut}
        role="presentation"
        {...props}
      >
        <StyledThumbnail
          $backgroundUrl={backgroundUrl}
          $gradientBackground={gradientBackground}
          data-testid="thumbnail-inner"
        >
          {isNotNil(children) ? children : null}
          {thumbnailContent}
        </StyledThumbnail>
      </StyledThumbnailContainer>
    );
  },
);

Thumbnail.displayName = 'Thumbnail';
